import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cn} from '../lib/cn';
import {TimelineContext, useTimeline} from './TimelineContext';

export interface TimelineProps extends React.ComponentPropsWithoutRef<'ol'> {
  orientation?: 'horizontal' | 'vertical';
}

function Timeline({className, orientation = 'vertical', ...props}: TimelineProps) {
  return (
    <TimelineContext.Provider value={{orientation}}>
      <ol
        data-slot="timeline"
        role="list"
        data-orientation={orientation}
        className={cn('flex', orientation === 'vertical' && 'flex-col', className)}
        {...props}
      />
    </TimelineContext.Provider>
  );
}

export interface TimelineItemProps extends React.ComponentPropsWithoutRef<'li'> {
  asChild?: boolean;
}

function TimelineItem({className, asChild, ...props}: TimelineItemProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'li';

  return (
    <Comp
      data-slot="timeline-item"
      data-orientation={orientation}
      className={cn('flex gap-4', orientation === 'horizontal' && 'flex-col', className)}
      {...props}
    />
  );
}

export interface TimelineSeparatorProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
}

function TimelineSeparator({className, asChild, ...props}: TimelineSeparatorProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-separator"
      data-orientation={orientation}
      className={cn(
        'flex items-center',
        orientation === 'vertical' && 'flex-col',
        className,
      )}
      {...props}
    />
  );
}

export interface TimelineDotProps extends React.ComponentPropsWithoutRef<'div'> {
  variant?: 'default' | 'outline';
  asChild?: boolean;
}

function TimelineDot({
  variant = 'default',
  className,
  asChild,
  ...props
}: TimelineDotProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-dot"
      data-orientation={orientation}
      className={cn(
        `flex size-4 items-center justify-center empty:after:block empty:after:rounded-full
        empty:after:outline-current [&_svg:not([class*='size-'])]:size-4`,
        orientation === 'vertical' && 'mt-[1px]',
        variant === 'default' && 'empty:after:size-2.5 empty:after:bg-current',
        variant === 'outline' && 'empty:after:size-2 empty:after:outline',
        className,
      )}
      {...props}
    />
  );
}

export interface TimelineConnectorProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
}

function TimelineConnector({className, asChild, ...props}: TimelineConnectorProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-connector"
      data-orientation={orientation}
      className={cn(
        'flex-1 bg-[var(--timeline-connector-bg)]',
        orientation === 'vertical' && 'w-0.5',
        orientation === 'horizontal' && 'h-0.5',
        className,
      )}
      {...props}
    />
  );
}

export interface TimelineContentProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
}

function TimelineContent({className, asChild, ...props}: TimelineContentProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-content"
      data-orientation={orientation}
      className={cn(
        'flex-1',
        orientation === 'vertical' && 'pb-7 first:text-right last:text-left',
        orientation === 'horizontal' && 'pr-7',
        className,
      )}
      {...props}
    />
  );
}

export interface TimelineTitleProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
}

function TimelineTitle({className, asChild, ...props}: TimelineTitleProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-title"
      data-orientation={orientation}
      className={className}
      {...props}
    />
  );
}

export interface TimelineDescriptionProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
}

function TimelineDescription({className, asChild, ...props}: TimelineDescriptionProps) {
  const {orientation} = useTimeline();
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="timeline-description"
      data-orientation={orientation}
      className={cn('text-[0.8em] text-[var(--timeline-description-color)]', className)}
      {...props}
    />
  );
}

export {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineTitle,
  TimelineDescription,
};
